import React, { useState } from "react";
import { Menu, X, Volume2, VolumeX, Lock } from "lucide-react";
import { usePersona, PERSONAS } from "../lib/persona";
import { useAdaptive } from "../lib/adaptive";
import { setMuted, resumeAudio } from "../lib/audio";

// Primary navigation — only the destinations that matter to a first-time
// visitor. The full inventory is still available from the mobile drawer
// and the command palette (Cmd+K).
const PRIMARY = [
    { id: "fabric",      label: "Fabric" },
    { id: "simulator",   label: "Simulator" },
    { id: "sandbox",     label: "Sandbox" },
    { id: "command",     label: "Command" },
    { id: "singularity", label: "Singularity" },
];

// Full list for the mobile drawer
const ALL_SECTIONS = [
    { id: "fabric",      label: "Fabric" },
    { id: "backbone",    label: "Backbone" },
    { id: "simulator",   label: "Simulator" },
    { id: "products",    label: "Products" },
    { id: "developers",  label: "Developers" },
    { id: "sandbox",     label: "Sandbox" },
    { id: "command",     label: "Command" },
    { id: "singularity", label: "Singularity" },
    { id: "connect",     label: "Connect" },
];

export default function Nav() {
    const { persona, setPersona, soundOn, setSoundOn } = usePersona();
    const { activeSection } = useAdaptive();
    const [open, setOpen] = useState(false);

    React.useEffect(() => {
        setMuted(!soundOn);
        if (soundOn) resumeAudio();
    }, [soundOn]);

    const go = (id) => {
        const el = document.getElementById(id);
        if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
        setOpen(false);
    };

    return (
        <>
            <header
                className="fixed top-0 inset-x-0 z-[90] bg-void/70 backdrop-blur-2xl border-b border-border/60"
                data-testid="nav-bar"
            >
                <div className="px-5 sm:px-8 h-12 flex items-center justify-between">
                    {/* Logo */}
                    <button
                        onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
                        className="flex items-center gap-2.5 group"
                        data-testid="nav-logo"
                    >
                        <span className="w-1.5 h-1.5 bg-signal animate-pulse-glow rounded-full" />
                        <span className="font-display font-black tracking-tighter text-base text-ghost group-hover:text-signal transition-colors">
                            APIB
                        </span>
                    </button>

                    {/* Primary nav — centered, minimal, with a single active underline */}
                    <nav className="hidden lg:flex items-center gap-7 absolute left-1/2 -translate-x-1/2">
                        {PRIMARY.map((s) => {
                            const active = activeSection === s.id;
                            return (
                                <button
                                    key={s.id}
                                    onClick={() => go(s.id)}
                                    data-testid={`nav-link-${s.id}`}
                                    className={`relative py-1 text-[11px] font-mono uppercase tracking-[0.22em] transition-colors ${
                                        active ? "text-ghost" : "text-muted hover:text-ghost"
                                    }`}
                                >
                                    {s.label}
                                    <span
                                        className={`absolute -bottom-px left-0 right-0 h-px bg-signal transition-opacity duration-300 ${
                                            active ? "opacity-100" : "opacity-0"
                                        }`}
                                    />
                                </button>
                            );
                        })}
                    </nav>

                    {/* Right side — persona, audio, admin, CTA */}
                    <div className="flex items-center gap-3">
                        <div
                            className="hidden md:flex items-center gap-3"
                            data-testid="persona-selector"
                        >
                            {PERSONAS.map((p) => (
                                <button
                                    key={p.id}
                                    onClick={() => setPersona(p.id)}
                                    data-testid={`persona-${p.id}`}
                                    title={p.full}
                                    className={`text-[10px] font-mono tracking-[0.22em] uppercase transition-colors ${
                                        persona === p.id
                                            ? "text-signal"
                                            : "text-muted/70 hover:text-ghost"
                                    }`}
                                >
                                    {p.label}
                                </button>
                            ))}
                        </div>

                        <span className="hidden md:inline w-px h-4 bg-border/60" aria-hidden />

                        <button
                            onClick={() => setSoundOn(!soundOn)}
                            data-testid="sound-toggle"
                            className="w-7 h-7 flex items-center justify-center text-muted hover:text-signal transition-colors"
                            title="Voice narrator"
                        >
                            {soundOn ? <Volume2 size={14} /> : <VolumeX size={14} />}
                        </button>

                        <a
                            href="/admin/login"
                            data-testid="nav-admin"
                            title="Admin console"
                            className="hidden md:inline-flex w-7 h-7 items-center justify-center text-muted hover:text-signal transition-colors"
                        >
                            <Lock size={12} />
                        </a>

                        <button
                            data-testid="nav-cta"
                            onClick={() => go("connect")}
                            className="hidden sm:inline-flex items-center gap-2 px-3.5 py-1.5 bg-signal text-void text-[10px] font-mono uppercase tracking-[0.22em] hover:bg-signal-soft transition-colors rounded-full"
                        >
                            Connect
                        </button>

                        <button
                            onClick={() => setOpen(true)}
                            data-testid="nav-menu-open"
                            className="lg:hidden w-7 h-7 flex items-center justify-center text-ghost"
                        >
                            <Menu size={16} />
                        </button>
                    </div>
                </div>
            </header>

            {/* Mobile drawer — full inventory still reachable */}
            {open && (
                <div
                    className="fixed inset-0 z-[100] bg-void/95 backdrop-blur-xl lg:hidden"
                    data-testid="nav-menu-drawer"
                >
                    <button
                        onClick={() => setOpen(false)}
                        className="absolute top-4 right-4 w-10 h-10 border border-border flex items-center justify-center"
                        data-testid="nav-menu-close"
                    >
                        <X size={18} />
                    </button>
                    <div className="pt-24 px-6 space-y-2">
                        {ALL_SECTIONS.map((s) => (
                            <button
                                key={s.id}
                                onClick={() => go(s.id)}
                                className="block w-full text-left font-display text-3xl font-bold tracking-tighter hover:text-signal transition-colors py-2"
                                data-testid={`nav-mobile-${s.id}`}
                            >
                                {s.label}
                            </button>
                        ))}
                    </div>
                </div>
            )}
        </>
    );
}
